// Migrated from Dayforce: UI\Framework\Scripts\Framework\Modules\DateUtil.ts

import {
    defaultInitializeLocalizeInstance,
    GetLocalizeInstance,
} from "@ceridianhcm/globalization";

/**
 * JSON date is old server format. Shouldn't be used anymore but still need to support.
 */
const JSON_DATE_REGEXP = /^\/Date\(((-?\d+)\))\/$/;
/**
 * Default string format to/from server is ISO format.
 */
const ISO_STRING_REGEXP =
    /^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d\.?(\d){0,3}([-+]?\d\d:?\d\d)?\w?$/;
/**
 * store up here to avoid unnecessary instantiation
 */
const HOUR_REGEXP = /(\d+):(\d+)/;

/**
 * Copied from `toISOString()` in [dojo-debug\1.10.4\dojo\date\stamp.js](http://tdc1tfsapp01:8080/tfs/defaultcollection/Platform/_git/third-party?path=%2Flibraries%2Fdojo-debug%2F1.10.4%2Fdojo%2Fdate%2Fstamp.js&version=GBmaster&line=102&lineEnd=141&lineStartColumn=1&lineEndColumn=3&lineStyle=plain).
 * Note that this is NOT equivalent to [ES6's native toISOString()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toISOString)
 */
function _toISOString(/*Date*/ dateObject, /*__Options?*/ options) {
    // summary:
    //		Format a Date object as a string according a subset of the ISO-8601 standard
    //
    // description:
    //		When options.selector is omitted, output follows [RFC3339](http://www.ietf.org/rfc/rfc3339.txt)
    //		The local time zone is included as an offset from GMT, except when selector=='time' (time without a date)
    //		Does not check bounds.  Only years between 100 and 9999 are supported.
    //
    // dateObject:
    //		A Date object

    var _ = function (n) {
        return n < 10 ? "0" + n : n;
    };
    options = options || {};
    var formattedDate = [],
        getter = options.zulu ? "getUTC" : "get",
        date = "";
    if (options.selector != "time") {
        var year = dateObject[getter + "FullYear"]();
        date = [
            "0000".substr((year + "").length) + year,
            _(dateObject[getter + "Month"]() + 1),
            _(dateObject[getter + "Date"]()),
        ].join("-");
    }
    formattedDate.push(date);
    if (options.selector != "date") {
        var time = [
            _(dateObject[getter + "Hours"]()),
            _(dateObject[getter + "Minutes"]()),
            _(dateObject[getter + "Seconds"]()),
        ].join(":");
        var millis = dateObject[getter + "Milliseconds"]();
        if (options.milliseconds) {
            time += "." + (millis < 100 ? "0" : "") + _(millis);
        }
        if (options.zulu) {
            time += "Z";
        } else if (options.selector != "time") {
            var timezoneOffset = dateObject.getTimezoneOffset();
            var absOffset = Math.abs(timezoneOffset);
            time +=
                (timezoneOffset > 0 ? "-" : "+") +
                _(Math.floor(absOffset / 60)) +
                ":" +
                _(absOffset % 60);
        }
        formattedDate.push(time);
    }
    return formattedDate.join("T"); // String
}

/**
 * Copied from `parse()` in [dojo-debug\1.10.4\dojo\date\locale.js](http://tdc1tfsapp01:8080/tfs/defaultcollection/Platform/_git/third-party?path=%2Flibraries%2Fdojo-debug%2F1.10.4%2Fdojo%2Fdate%2Flocale.js&version=GBmaster&line=294&lineEnd=294&lineStartColumn=1&lineEndColumn=14&lineStyle=plain).
 * We were hoping to replace this with the `parseDateString()` in @platform/globalization/LocalizeUtil, but it doesn't support the format specified in dojo.
 * Globalization team think it's hacky to add support for that since it goes against their principle of enforcing standards.
 */
function _parse(/*String*/ value, /*__FormatOptions?*/ options) {
    // summary:
    //		Convert a properly formatted string to a primitive Date object,
    //		using locale-specific settings.
    //
    // description:
    //		Create a Date object from a string using a known localized pattern.
    //		By default, this method parses looking for both date and time in the string.
    //		Formatting patterns are chosen appropriate to the locale.  Different
    //		formatting lengths may be chosen, with "full" used by default.
    //		Custom patterns may be used or registered with translations using
    //		the dojo/date/locale.addCustomFormats() method.
    //
    //		Formatting patterns are implemented using [the syntax described at
    //		unicode.org](http://www.unicode.org/reports/tr35/tr35-4.html#Date_Format_Patterns)
    //		When two digit years are used, a century is chosen according to a sliding
    //		window of 80 years before and 20 years after present year, for both `yy` and `yyyy` patterns.
    //		year < 100CE requires strict mode.
    //
    // value:
    //		A string representation of a date

    // remove non-printing bidi control chars from input and pattern
    var controlChars = /[\u200E\u200F\u202A\u202E]/g,
        info = exports._parseInfo(options),
        tokens = info.tokens,
        bundle = info.bundle,
        re = new RegExp(
            "^" + info.regexp.replace(controlChars, "") + "$",
            info.strict ? "" : "i"
        ),
        match = re.exec(value && value.replace(controlChars, ""));

    if (!match) {
        return null;
    } // null

    var widthList = ["abbr", "wide", "narrow"],
        result = [1970, 0, 1, 0, 0, 0, 0], // will get converted to a Date at the end
        amPm = "",
        valid = match.every(function (v: any, i) {
            if (!i) {
                return true;
            }
            var token = tokens[i - 1],
                l = token.length,
                c = token.charAt(0);
            switch (c) {
                case "y":
                    if (l != 2 && options.strict) {
                        //interpret year literally, so '5' would be 5 A.D.
                        result[0] = v;
                    } else {
                        if (v < 100) {
                            v = Number(v);
                            //choose century to apply, according to a sliding window
                            //of 80 years before and 20 years after present year
                            var year = "" + new Date().getFullYear(),
                                century = parseInt(year.substring(0, 2)) * 100,
                                cutoff = Math.min(
                                    Number(year.substring(2, 4)) + 20,
                                    99
                                );
                            result[0] =
                                v < cutoff ? century + v : century - 100 + v;
                        } else {
                            //we expected 2 digits and got more...
                            if (options.strict) {
                                return false;
                            }
                            //interpret literally, so '150' would be 150 A.D.
                            //also tolerate '1950', if 'yyyy' input passed to 'yy' format
                            result[0] = v;
                        }
                    }
                    break;
                case "M":
                case "L":
                    if (l > 2) {
                        var months =
                            bundle[
                                "months-" +
                                    (c == "L" ? "standAlone" : "format") +
                                    "-" +
                                    widthList[l - 3]
                            ].concat();
                        if (!options.strict) {
                            //Tolerate abbreviating period in month part
                            //Case-insensitive comparison
                            v = v.replace(".", "").toLowerCase();
                            months = months.map(function (s) {
                                return s.replace(".", "").toLowerCase();
                            });
                        }
                        v = months.indexOf(v);
                        if (v == -1) {
                            //						console.log("dojo/date/locale.parse: Could not parse month name: '" + v + "'.");
                            return false;
                        }
                    } else {
                        v--;
                    }
                    result[1] = v;
                    break;
                case "E":
                case "e":
                case "c":
                    var days =
                        bundle[
                            "days-" +
                                (c == "c" ? "standAlone" : "format") +
                                "-" +
                                widthList[l - 3]
                        ].concat();
                    if (!options.strict) {
                        //Case-insensitive comparison
                        v = v.toLowerCase();
                        days = days.map(function (d) {
                            return d.toLowerCase();
                        });
                    }
                    v = days.indexOf(v);
                    if (v == -1) {
                        //					console.log("dojo/date/locale.parse: Could not parse weekday name: '" + v + "'.");
                        return false;
                    }

                    //TODO: not sure what to actually do with this input,
                    //in terms of setting something on the Date obj...?
                    //without more context, can't affect the actual date
                    //TODO: just validate?
                    break;
                case "D":
                    result[1] = 0;
                // fallthrough...
                case "d":
                    result[2] = v;
                    break;
                case "a": //am/pm
                    var am = options.am || bundle["dayPeriods-format-wide-am"],
                        pm = options.pm || bundle["dayPeriods-format-wide-pm"];
                    if (!options.strict) {
                        var period = /\./g;
                        v = v.replace(period, "").toLowerCase();
                        am = am.replace(period, "").toLowerCase();
                        pm = pm.replace(period, "").toLowerCase();
                    }
                    if (options.strict && v != am && v != pm) {
                        //					console.log("dojo/date/locale.parse: Could not parse am/pm part.");
                        return false;
                    }

                    // we might not have seen the hours field yet, so store the state and apply hour change later
                    amPm = v == pm ? "p" : v == am ? "a" : "";
                    break;
                case "K": //hour (1-24)
                    if (v == 24) {
                        v = 0;
                    }
                // fallthrough...
                case "h": //hour (1-12)
                case "H": //hour (0-23)
                case "k": //hour (0-11)
                    //TODO: strict bounds checking, padding
                    if (v > 23) {
                        //					console.log("dojo/date/locale.parse: Illegal hours value");
                        return false;
                    }

                    //in the 12-hour case, adjusting for am/pm requires the 'a' part
                    //which could come before or after the hour, so we will adjust later
                    result[3] = v;
                    break;
                case "m": //minutes
                    result[4] = v;
                    break;
                case "s": //seconds
                    result[5] = v;
                    break;
                case "S": //milliseconds
                    result[6] = v;
                //				break;
                //			case 'w':
                //TODO				var firstDay = 0;
                //			default:
                //TODO: throw?
                //				console.log("dojo/date/locale.parse: unsupported pattern char=" + token.charAt(0));
            }
            return true;
        });

    var hours = +result[3];
    if (amPm === "p" && hours < 12) {
        result[3] = hours + 12; //e.g., 3pm -> 15
    } else if (amPm === "a" && hours == 12) {
        result[3] = 0; //12am -> 0
    }

    //TODO: implement a getWeekday() method in order to test
    //validity of input strings containing 'EEE' or 'EEEE'...

    var dateObject = new Date(
        result[0],
        result[1],
        result[2],
        result[3],
        result[4],
        result[5],
        result[6]
    ); // Date
    if (options.strict) {
        dateObject.setFullYear(result[0]);
    }

    // Check for overflow.  The Date() constructor normalizes things like April 32nd...
    //TODO: why isn't this done for times as well?
    var allTokens = tokens.join(""),
        dateToken = allTokens.indexOf("d") != -1,
        monthToken = allTokens.indexOf("M") != -1;

    if (
        !valid ||
        (monthToken && dateObject.getMonth() > result[1]) ||
        (dateToken && dateObject.getDate() > result[2])
    ) {
        return null;
    }

    // Check for underflow, due to DST shifts.  See #9366
    // This assumes a 1 hour dst shift correction at midnight
    // We could compare the timezone offset after the shift and add the difference instead.
    if (
        (monthToken && dateObject.getMonth() < result[1]) ||
        (dateToken && dateObject.getDate() < result[2])
    ) {
        // dateObject = date.add(dateObject, "hour", 1);
        dateObject = new Date(dateObject.getTime() + 60 * 60 * 1000);
    }

    return dateObject; // Date
}

/*
 * This is a modification of fromISOString, which fixes 2 issues:
 * 1. Daylight saving is adjusted before time zone. DateUtil.toDate("2015-03-08T02:00:00.000+00:00") and new Date("2015-03-08T02:00:00.000+00:00") return different result
 * 2. In Safari, the daylight saving is not adjusted properly. DateUtil.toDate("2015-03-08T05:00:00.000Z") returns "3/8/2015 01:00:00AM". The correct date is "3/8/2015 12:00:00AM"
 * Both issues happens if the date is the start of daylight saving
 */
function _fromISOString(date, defaultTime?): Date | null {
    let match =
            /^(?:(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?)?(?:T(\d{2}):(\d{2})(?::(\d{2})(.\d+)?)?((?:[+-](\d{2}):(\d{2}))|Z)?)?$/.exec(
                date
            ),
        result = null;

    if (match) {
        match.shift();
        if (match[1]) {
            let m = parseInt(match[1], 10);
            m--;
            match[1] = m.toString();
        } // Javascript Date months are 0-based
        if (match[6]) {
            let ms = parseFloat(match[6]);
            ms *= 1000;
            match[6] = ms.toString();
        } // Javascript Date expects fractional seconds as milliseconds

        if (defaultTime) {
            // mix in defaultTime.  Relatively expensive, so use || operators for the fast path of defaultTime === 0
            defaultTime = new Date(defaultTime);

            const dateOptionArray = [
                "FullYear",
                "Month",
                "Date",
                "Hours",
                "Minutes",
                "Seconds",
                "Milliseconds",
            ];
            const mappedDateOptionArray = dateOptionArray.map((prop) =>
                defaultTime["get" + prop]()
            );
            mappedDateOptionArray.forEach((value, index) => {
                match[index] = match[index] || value;
            });
        }

        // Get UTC timestamp
        const timestamp = Date.UTC(
            Number(match[0]) || 1970,
            Number(match[1]) || 0,
            Number(match[2]) || 1,
            Number(match[3]) || 0,
            Number(match[4]) || 0,
            Number(match[5]) || 0,
            Number(match[6]) || 0
        );
        let offset = 0;
        let zoneSign = match[7] && match[7].charAt(0);
        //Formats: Explicit TimeZone => 2000-01-01T00:00:00.000+00:00; UTC => 2000-01-01T00:00:00.000Z (Results from date.toISOString)
        if (zoneSign != "Z") {
            offset = (Number(match[8]) || 0) * 60 + (Number(match[9]) || 0);
            if (zoneSign != "-") {
                offset *= -1;
            }
            zoneSign = null; //Ignore TimeZone and Daylight Saving !!! For some TimeZones, using 'offset' results in undesired of the Date and/or Year.
        }
        if (zoneSign) {
            //UTC => 2000-01-01T00:00:00.000Z (Results from date.toISOString)
            // Adjust timezone offset
            result = new Date(timestamp + offset * 60000);
        } else {
            // If there is no timezome sign, then create a new date with year, month, day, hour, min, sec, and millisec.
            // If Year is < 100, the JS Date adds 1900; yielding a Min Year = 1900
            result = new Date(
                Number(match[0]) || 1970,
                Number(match[1]) || 0,
                Number(match[2]) || 1,
                Number(match[3]) || 0,
                Number(match[4]) || 0,
                Number(match[5]) || 0,
                Number(match[6]) || 0
            );
        }

        // Set full year
        let y = parseInt(match[0]);
        if (y < 100) {
            result.setFullYear(y || 1970);
            //TBD: Safari and FireFox
            //Avoid Scripts / Overrides / Date.set <???> functions which use Timestamp to adjust the date based on Client Timezone and Daylight Savings. Use setUTCFullYear
            //Depending on the Client's Timezone, the side effects can adjust the Year and Date by -1 or +1.
            //result.setUTCFullYear(y || 1970);
        }
    }

    return result; // Date or null
}

/**
 * Checks if the provided string is in a predefined and recognized date format (ISO or JSON)
 * @param value The string to check if it's a recognized date format
 * @returns Boolean on if this is a valid date for our application
 */
function isDateString(value: string) {
    //although other dates may be "valid", we want to be strict in only checking dates that we recognize explicitly.
    return JSON_DATE_REGEXP.test(value) || ISO_STRING_REGEXP.test(value);
}

/**
 * Converts the provided date into an ISO string format
 * @param date The local date to convert
 */
function toString(date: Date): string {
    let dateString = null;
    if (date) {
        // using dojo/date/stamp _toISOString()
        dateString = _toISOString(date, { milliseconds: true });
        // Update: date.toISOString() formats slightly different, so not using that for now
        // Therefore we must intervene with certain pre-standardized timezone values
        // that have caused errors server side
        // Can avoid this if using default times that start at 1970 instead of year one.
        if (date.getTimezoneOffset() % 1 != 0) {
            // For comma languages
            let separator = (1.1).toLocaleString().substring(1, 2);
            dateString = dateString.substring(
                0,
                dateString.lastIndexOf(separator)
            );
        }
    }
    return dateString;
}

/**
 * Accepts a string and converts it into a local date object.
 * The string can be a an ISO string, JSON date format, UTC date, or UTC time in milliseconds from the JS epoch (Jan 1, 1970).
 * @param date The UTC string
 * @return The local date object representing the passed date
 */
function toDate(date: string): Date {
    if (date == null || date === "") return null;

    let result: any = null;

    //if it's an iso string, use fromISOString function to handle walltime
    if (ISO_STRING_REGEXP.test(date)) {
        result = _fromISOString(date);
    } else {
        const res = JSON_DATE_REGEXP.exec(date);
        if (res && res.length >= 3) date = res[2];
    }

    //either parsing failed or no valid regex match was found -> try more parsing
    if (!result || !(result instanceof Date)) {
        const dateAsNumber = Number(date);

        result = !isNaN(dateAsNumber) ? dateAsNumber : Date.parse(date) || null;

        if (result == null) {
            defaultInitializeLocalizeInstance().then(() => {
                const LocalizeInstance = GetLocalizeInstance();
                //TFS 901526 hack to handle double submission of same value
                //converts already formatted localized date string back into javascript date object to prevent stack overflow
                //console warning added to notify dev & QA of failure if failure occurs
                //root cause of double submissions of the same value should be investigated and fixed within framework
                result = !isNaN(dateAsNumber)
                    ? dateAsNumber
                    : LocalizeInstance.parseDateString(date, {
                          date: "short",
                      }) || null;

                if (result == null) {
                    // Example string formatted in above defined patterns: "10/19/2021, 12:24:55.604Eastern Daylight Time"
                    var customFormatParse = _parse(date, {
                        datePattern: "MM/dd/yyyy",
                        timePattern: "HH:mm:ss.SSSz",
                    });

                    if (customFormatParse) {
                        result = Date.UTC(
                            customFormatParse.getFullYear(),
                            customFormatParse.getMonth(),
                            customFormatParse.getDate(),
                            customFormatParse.getHours(),
                            customFormatParse.getMinutes(),
                            customFormatParse.getSeconds(),
                            customFormatParse.getMilliseconds()
                        );
                    }
                }
            });
        }
        result = new Date(result);
    }

    return result;
}

/**
 * Converts a date from a JS date object to an ASP date string in the format of:
 * /Date(numberOfMsSinceEpoch)/
 * e.g., /Date(12392178)/
 * @param date The JS date object to convert
 * @returns The ASP date string
 */
function toJSON(date: Date): string {
    var ret = "";
    if (date)
    {
        // ORIGINAL
        // ret = str.substitute("/Date(${0})/", [date.getTime()]);
        // NEW
        ret = "/Date("+date.getTime()+")/";
    }
    return ret;
}

/**
 * Null/undefined safe way of checking date equally
 * Internally calls getTime() and compares that numeric value
 * @param dateA The first date to compare with
 * @param dateB The second date to compare against dateA
 * @param dateOnly Optional. If true, will only compare literal date values (no hours, minutes or seconds)
 * @returns True if the two dates are equal, false otherwise
*/
function isEqual(a: Date, b: Date, dateOnly?:boolean): boolean
{
    if (dateOnly === true)
    {
        //if we are checking date equality only,
        //clone the dates (this is a lightweight procedure) first
        a = clone(a, true);
        b = clone(b, true);
    }

    var valueA = a && a.getTime ? a.getTime() : (<any>a);
    var valueB = b && b.getTime ? b.getTime() : (<any>b);
    return valueA === valueB;
}

// Copied from https://dev.azure.com/Ceridian/Platform/_git/third-party?path=/libraries/dojo-debug/1.10.4/dojo/date.js&version=GBmaster&line=80&lineEnd=111&lineStartColumn=1&lineEndColumn=3&lineStyle=plain&_a=contents
// Utility methods to do arithmetic calculations with Dates
function dojoDateCompare(/*Date*/date1, /*Date?*/date2, /*String?*/portion){
	// summary:
	//		Compare two date objects by date, time, or both.
	// description:
	//		Returns 0 if equal, positive if a > b, else negative.
	// date1:
	//		Date object
	// date2:
	//		Date object.  If not specified, the current Date is used.
	// portion:
	//		A string indicating the "date" or "time" portion of a Date object.
	//		Compares both "date" and "time" by default.  One of the following:
	//		"date", "time", "datetime"

	// Extra step required in copy for IE - see #3112
	date1 = new Date(+date1);
	date2 = new Date(+(date2 || new Date()));

	if(portion == "date"){
		// Ignore times and compare dates.
		date1.setHours(0, 0, 0, 0);
		date2.setHours(0, 0, 0, 0);
	}else if(portion == "time"){
		// Ignore dates and compare times.
		date1.setFullYear(0, 0, 0);
		date2.setFullYear(0, 0, 0);
	}

	if(date1 > date2){ return 1; } // int
	if(date1 < date2){ return -1; } // int
	return 0; // int
};

// TODO
/**
 * Compares the provided date objects and returns a number representing the comparison result
 * 0 - The dates are equal
 * -1 - dateA is less than dateB
 * 1 - dateA is greater than dateB
 * @param dateA The first JS date object to compare
 * @param dateB The second JS date object to compare
 * @param dateOnly Optional boolean to check only the date (not time) portion of the provided dates. I.e. ignore differences in hours, minutes and seconds
 * @returns number representing the comparison output (similar to C# compare)
 */
function compare(dateA: Date, dateB: Date, dateOnly?: boolean)
{
    // ORIGINAL
    // return dojoDate.compare(dateA, dateB, dateOnly === true ? 'date' : 'datetime');
    // NEW
    return dojoDateCompare(dateA, dateB, dateOnly === true ? 'date' : 'datetime');
}

/**
 * Creates a new date object based from an existing date
 * Will provide a clone down to the millisecond. If true is passed for the
 * second parameter, only values up to the day (no hours, minutes or seconds) will be copied
 * @param date The date object to clone
 * @param dateOnly Optional. If true, will only create a clone with Y/M/D 
 * @returns New object cloned from the existing date
 */
function clone(date: Date, dateOnly?: boolean): Date
{
    var ret = null;
    if (date)
    {
        if (dateOnly === true)
            ret = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
        else
            ret = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    }
    return ret;
}

/**
 * Checks if the two date ranges intersects at any point. Note, perfectly adjacent times do not count as intersecting!
 * @returns True if the date ranges intersect, false otherwise
 */
function intersects(startA: Date, endA: Date, startB: Date, endB: Date)
{
    //one of these is bad data - return immediately false
    if (!(startA instanceof Date) || !(endA instanceof Date) || !(startB instanceof Date) || !(endB instanceof Date))
        return false;

    //internal function to check collision in one direction
    var collidesOneWay = function (a1, a2, b1, b2)
    {
        //explicitly ignore ajacent times (e.g. 4-5PM, 5-6PM), needed for calendar
        return (a1 >= b1 && a1 < b2)
            || (a2 > b1 && a1 < b2);
    
    }

    //check both directions of collision
    return collidesOneWay(startA, endA, startB, endB) || collidesOneWay(startB, endB, startA, endA);

}

// TODO - getDatePattern does not exist on type 'LocalizeSingleton' - Build Error
/**
 * Localizes the time.
 * @returns a JSON object of time tokens
 * { hours: hour
 *   minutes: minute
 *   ampmLabel: the text to show for AM/PM
 *   calendarCss: this is a calendar specific css class }
 */
// function getLocalizedTimeTokens(date: Date) 
// {
//     defaultInitializeLocalizeInstance().then(() => {
//         const LocalizeInstance = GetLocalizeInstance();
//         var dateString = LocalizeInstance.formatDate(date, 't');
//         var execResult = HOUR_REGEXP.exec(dateString);

//         //show ampm if the current date culture has t in its "t" pattern. That's a lot of t's.
//         var showAmPm = /.*t+.*/.test(LocalizeInstance.getDatePattern('t'));

//         //use regexp to capture number to the left of the semicolon in a capture group - JS puts capture group starting at index 1
//         //if regexp malformed or something failed, fallback to getHours() call as a precaution
//         return execResult && execResult.length && execResult.length >= 3 ?
//             {
//                 hours: execResult[1], //hour
//                 minutes: execResult[2], //minute
//                 ampmLabel: showAmPm ? LocalizeInstance.formatDate(date, 'tt') : '', //ampm
//                 calendarCss: date.getHours() < 12 ? 'morning' : 'afternoon' //css class used to conditionally hide the ampm node
//             } :
//             {
//                 hours: date.getHours().toString(), //hour
//                 minutes: date.getMinutes().toString(), //minute
//                 ampmLabel: showAmPm ? LocalizeInstance.formatDate(date, 'tt') : '', //ampm
//                 calendarCss: date.getHours() < 12 ? 'morning' : 'afternoon' //css class used to conditionally hide the ampm node
//             };
//     });
// }

/**
 * Utility method to convert fields on the given object into Date using toDate() method
 * @returns the object passed as a fist parameter
 * @param obj object which has fields to convert to Date (typically straight from JSON deserialization)
 * @param dateField array of field names to parse
 */
function parseDates(obj: any, dateFields?: string[]) {
    dateFields = Array.isArray(dateFields) ? dateFields : Array.prototype.splice.call(arguments, 1);
    for (var i = 0; i < dateFields.length; i++) {
        var field = dateFields[i];
        obj[field] = this.toDate(obj[field]);
    }
    return obj;
}

/**
 * Helper method to determine if given date is between start and end dates. Includes logic for dates without time portion.
 * @returns true if @now is between @start and @end, otherwise false.
 * @param start lower bound to check against - inclusive
 * @param end upper bound to check against - exclusive. Assumed to be next day if there is not time portion.
 * @param now the effective date use in computation. Optional.
 */
function isEffective(start: Date, end: Date, now?: Date) {
    now = now || new Date();
    if (start && now < start)
        return false;
    if (!end) {
        return true;
    }
    if (end.getHours() + end.getMinutes() + end.getSeconds() === 0) {
        // special case when effective end does not have time portion 
        end = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    }
    return now < end;
}

/**
 * Helper method to determine the particular week number that the given date belongs to.
 * if 1 January is on a Monday, Tuesday, Wednesday or Thursday, it is in week 01. If 1 January is on a Friday, Saturday or Sunday,
 * it is in week 52 or 53 of the previous year (there is no week 00). 28 December is always in the last week of its year.
 * @returns week number for the particular supplied date, according to ISO week-numbering year
 * @param date date to search week number for
 * @param startDayOfWeek start day of week to be taken into account for when calculating the week number, if not specified, will use monday (1) as reference
 */
function getISOWeekNumber(date: Date) : number {
    var t: any = new Date(date.valueOf());
    var dn = (date.getDay() + 6) % 7;
    t.setDate(t.getDate() - dn + 3);
    var firstThurs = t.valueOf();
    t.setMonth(0, 1);
    if (t.getDay() !== 4) {
        t.setMonth(0, 1 + ((4 - t.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThurs - t) / 604800000);
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform a Date value into a short string describing the date in the current locale.
 */
function toShortDate(date: any) {
    defaultInitializeLocalizeInstance().then(() => {
        const LocalizeInstance = GetLocalizeInstance();
        if (typeof date === "string") {
            date = DateUtil.toDate(date);
        }
    
        if (!(date instanceof Date)) {
            return new String();
        }
        return LocalizeInstance.formatDate(date, LocalizeInstance.FORMAT_DATE_SHORT);
    });
    
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform a Date value into a long string describing the date in the current locale.
 */
function toLongDate(date: any) {
    defaultInitializeLocalizeInstance().then(() => {
        const LocalizeInstance = GetLocalizeInstance();
        if (typeof date === "string") {
            date = DateUtil.toDate(date);
        }

        if (!(date instanceof Date)) {
            return new String();
        }

        return LocalizeInstance.formatDate(date, LocalizeInstance.FORMAT_DATE_LONG);
    });
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform a DateTime value into a string describing the time in the current locale.
 */
function toTime(date: any) {
    defaultInitializeLocalizeInstance().then(() => {
        const LocalizeInstance = GetLocalizeInstance();
        if (typeof date === "string") {
            date = DateUtil.toDate(date);
        }

        if (!(date instanceof Date)) {
            return new String();
        }
        return LocalizeInstance.formatDate(date, LocalizeInstance.FORMAT_TIME_SHORT);
});
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Transform a DateTime value into a string describing the day and time in the current locale.
 */
function toDayTime(date: any) {
    defaultInitializeLocalizeInstance().then(() => {
        const LocalizeInstance = GetLocalizeInstance();
        if (typeof date === "string") {
            date = DateUtil.toDate(date);
        }

        if (!(date instanceof Date)) {
            return new String();
        }

        return LocalizeInstance.formatDate(date, LocalizeInstance.FORMAT_DATETIME_SHORT);
    });
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Take the day from a Date object and localize it in the current locale settings as the day of week
 */
function toDayOfWeek(date: any) {
    if (typeof date === "string") {
        date = DateUtil.toDate(date);
    }

    if (!(date instanceof Date)) {
        return new String();
    }

    var options = {
        selector: 'date',
        datePattern: 'EEEE'
    };

    //TODO
    // return locale.format(date, options);
}

//FROM C:\Dayforce\git\LegacyFrontEnd\src\Platform\Framework\Scripts\Framework\Modules\Transformers.ts
/**
 * Take the day from a Date object and localize it in the current locale settings
 */
function toDay(date: any) {
    if (typeof date === "string") {
        date = DateUtil.toDate(date);
    }

    if (!(date instanceof Date)) {
        return new String();
    }

    var options = {
        selector: 'date',
        datePattern: 'd'
    };
 
    //TODO
    // return locale.format(date, options);
}

/**
 * DateUtil is a simple module with basic date formatting logic within
 * Since our MVC server side depends on UTC date/times being formatted with a rigid string structure
 * we need a centralized place to do this.
 * @module
 */
export const DateUtil = {
    isDateString,
    toString,
    toDate,
    toJSON,
    isEqual,
    compare,
    clone,
    intersects,
    // getLocalizedTimeTokens,
    parseDates,
    isEffective,
    getISOWeekNumber,
    _fromISOString,
    toShortDate,
    toLongDate,
    toTime,
    toDayTime,
    toDayOfWeek,
    toDay
};
